/**
 * Sticky bottom navigation.
 *
 * Every Indian quick-commerce app puts its primary destinations in thumb reach
 * and leaves them there. It costs a strip of screen and buys two things: you
 * always know where you are, and nothing important is more than one tap away —
 * which is the difference between a one-minute-a-day habit and a chore.
 */

export type Tab = "larder" | "calendar" | "settings";

/**
 * `settings` is a Tab but NOT a nav item.
 *
 * It is reached from the top bar, which is the only way in — two doors to one
 * room cost a permanent quarter of the nav bar and taught nothing, because both
 * led to the same place. The two that remain are the ones you move BETWEEN;
 * settings is somewhere you visit and come back from.
 *
 * "Binned" was the third, and it is gone. A tab whose entire job was to show
 * you what you had wasted earned a permanent third of the nav bar to make
 * anyone who opened it feel worse. What was load-bearing about it — correcting
 * a presumption the app made on its own — moved into the larder, next to the
 * check-ins it belongs with.
 */
const TABS: ReadonlyArray<{ id: Tab; icon: string; label: string; testid: string }> = [
  { id: "larder", icon: "🧺", label: "Larder", testid: "nav-larder" },
  { id: "calendar", icon: "🗓", label: "Calendar", testid: "open-calendar" },
];

export function BottomNav({
  active,
  onGo,
  badges,
}: {
  active: Tab;
  onGo: (tab: Tab) => void;
  /** Counts worth interrupting for. Zero renders nothing, not a "0". */
  badges?: Partial<Record<Tab, number>>;
}) {
  return (
    <nav className="bottomnav" aria-label="Sections">
      {/* No wordmark above these. The desktop rail briefly had one, and since
          the app is called Larder and so is the first tab, it printed the same
          word twice, stacked. The tab is the one that does something. */}
      {TABS.map((t) => {
        const count = badges?.[t.id] ?? 0;
        return (
          <button
            key={t.id}
            type="button"
            className={`navitem ${active === t.id ? "on" : ""}`}
            onClick={() => onGo(t.id)}
            aria-current={active === t.id ? "page" : undefined}
            data-testid={t.testid}
          >
            <span className="navicon" aria-hidden="true">
              {t.icon}
            </span>
            {/* A SIBLING of the icon, not a child of it, and that is
                load-bearing. The icon carries `filter: grayscale(...)` when its
                tab is unselected and `transform: scale(.9)` while pressed, and
                either one makes it the containing block for an absolutely
                positioned descendant — so a badge inside it anchored to the
                icon when idle and to the row when selected, and visibly jumped
                between the two. Out here it has one anchor: the row. */}
            {count > 0 && <span className="badge">{count > 9 ? "9+" : count}</span>}
            <span className="navlabel">{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
